import { memo, useEffect, useRef } from "react";

const TVWidget = memo(function TVWidget({ src, config, height = 520, testId }) {
    const ref = useRef(null);
    const configKey = JSON.stringify(config);

    useEffect(() => {
        const el = ref.current;
        if (!el || el.dataset.loaded === configKey) return;
        el.dataset.loaded = configKey;
        el.innerHTML = "";
        const widget = document.createElement("div");
        widget.className = "tradingview-widget-container__widget";
        widget.style.height = "100%";
        widget.style.width = "100%";
        el.appendChild(widget);
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.type = "text/javascript";
        script.innerHTML = configKey;
        el.appendChild(script);
    }, [src, configKey]);

    return (
        <div
            ref={ref}
            data-testid={testId}
            className="tradingview-widget-container w-full overflow-hidden"
            style={{ height }}
        />
    );
});

export const AdvancedChart = ({ symbol = "TVC:GOLD", height = 560, testId = "tv-advanced-chart" }) => (
    <TVWidget
        src="https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js"
        testId={testId}
        height={height}
        config={{
            autosize: true,
            symbol,
            interval: "D",
            timezone: "Etc/UTC",
            theme: "dark",
            style: "1",
            locale: "en",
            backgroundColor: "rgba(10, 10, 12, 1)",
            hide_side_toolbar: false,
            allow_symbol_change: true,
            support_host: "https://www.tradingview.com",
        }}
    />
);

export const MarketOverview = ({ height = 560, testId = "tv-market-overview" }) => (
    <TVWidget
        src="https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js"
        testId={testId}
        height={height}
        config={{
            colorTheme: "dark",
            dateRange: "12M",
            showChart: true,
            locale: "en",
            isTransparent: true,
            showSymbolLogo: true,
            showFloatingTooltip: true,
            width: "100%",
            height: "100%",
            plotLineColorGrowing: "rgba(16, 185, 129, 1)",
            plotLineColorFalling: "rgba(239, 68, 68, 1)",
            tabs: [
                {
                    title: "Commodities",
                    symbols: [
                        { s: "TVC:GOLD", d: "Gold" },
                        { s: "TVC:SILVER", d: "Silver" },
                        { s: "TVC:USOIL", d: "WTI Crude" },
                        { s: "TVC:UKOIL", d: "Brent Crude" },
                        { s: "NYMEX:HG1!", d: "Copper" },
                    ],
                },
                {
                    title: "Indices",
                    symbols: [
                        { s: "FOREXCOM:SPXUSD", d: "S&P 500" },
                        { s: "FOREXCOM:NSXUSD", d: "Nasdaq 100" },
                        { s: "FOREXCOM:DJI", d: "Dow 30" },
                        { s: "INDEX:FTSE", d: "FTSE 100" },
                        { s: "INDEX:DEU40", d: "DAX 40" },
                    ],
                },
                {
                    title: "ETFs",
                    symbols: [
                        { s: "AMEX:SPY", d: "S&P 500 ETF" },
                        { s: "NASDAQ:QQQ", d: "Nasdaq 100 ETF" },
                        { s: "AMEX:GLD", d: "Gold ETF" },
                        { s: "AMEX:SLV", d: "Silver ETF" },
                        { s: "AMEX:USO", d: "Oil ETF" },
                    ],
                },
            ],
        }}
    />
);

export const Screener = ({ height = 560, testId = "tv-screener" }) => (
    <TVWidget
        src="https://s3.tradingview.com/external-embedding/embed-widget-screener.js"
        testId={testId}
        height={height}
        config={{
            width: "100%",
            height: "100%",
            defaultColumn: "overview",
            defaultScreen: "most_capitalized",
            market: "america",
            showToolbar: true,
            colorTheme: "dark",
            locale: "en",
            isTransparent: true,
        }}
    />
);

export const EconomicCalendar = ({ height = 560, testId = "tv-economic-calendar" }) => (
    <TVWidget
        src="https://s3.tradingview.com/external-embedding/embed-widget-events.js"
        testId={testId}
        height={height}
        config={{
            colorTheme: "dark",
            isTransparent: true,
            width: "100%",
            height: "100%",
            locale: "en",
            importanceFilter: "0,1",
            countryFilter: "us,eu,gb,jp,ch,au,ca",
        }}
    />
);

export default TVWidget;
